/**
 * MapaCompradorPreview.jsx
 *
 * - Preview: renderiza JSX em tamanho real (largura total da página)
 * - Imprimir: busca o PDF do backend e abre em nova aba com iframe para print()
 */
import { useRef, useState, Fragment } from 'react'
import { Printer } from 'lucide-react'
import api from '../../api/client'
import AlterarComissaoModal from '../../components/modals/AlterarComissaoModal'
import mapasService from '../../services/mapasService'
import IsentarGTAModal from '../../components/modals/IsentarGTAModal'
import { openPdf, downloadPdf } from '../../utils/pdfUtils'
import PrazoComissao from '../../components/mapas/PrazoComissao'
import { criasTxt, criasTotais } from './criasUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

// ─── Formatadores ─────────────────────────────────────────────────────────────
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const num = v => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try {
    // Extrai apenas YYYY-MM-DD para evitar problemas de timezone
    const dateStr = String(s).split('T')[0]
    const [y, m, d] = dateStr.split('-')
    if (y && m && d) return `${d}/${m}/${y}`
    return new Date(s).toLocaleDateString('pt-BR')
  } catch { return s }
}

// ─── Estilos inline para o preview (tamanho real) ────────────────────────────
const S = {
  page: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: 13,
    color: '#000',
    background: '#fff',
    padding: '16px 24px 20px',
    borderBottom: '2px solid #e5e7eb',
    marginBottom: 0,
  },
  hdr: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    paddingBottom: 6, marginBottom: 6,
  },
  hdrLogo: { maxHeight: 64, maxWidth: 160, objectFit: 'contain' },
  hdrTitle: { flex: 1, padding: '0 20px', textAlign: 'center' },
  hdrT1: { fontSize: 16, fontWeight: 700 },
  hdrT2: { fontSize: 12, marginTop: 3 },
  hdrBank: { textAlign: 'right', fontSize: 11, lineHeight: 1.6 },
  compBlock: { marginBottom: 14 },
  compLbl: { fontSize: 11, fontWeight: 700, marginBottom: 3 },
  compVal: { fontSize: 12, lineHeight: 1.3 },
  tbl: { width: '100%', borderCollapse: 'collapse', marginBottom: 4, fontSize: 12 },
  th: { background: '#d0d0d0', border: '1px solid #999', padding: '2px 6px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  td: { border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'top' },
  tdR: { border: '1px solid #ccc', padding: '5px 6px', textAlign: 'right', verticalAlign: 'top' },
  tdC: { border: '1px solid #ccc', padding: '5px 6px', textAlign: 'center', verticalAlign: 'top' },
  desc: { fontSize: 10, color: '#555', marginTop: 2 },
  totais: { textAlign: 'right', fontSize: 12, lineHeight: 1.8, marginBottom: 14 },
  thPar: { background: '#e8e8e8', border: '1px solid #999', padding: '2px 6px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  dbVend: { border: '1px solid #ccc', padding: '8px 12px', fontSize: 12, marginTop: 10 },
  footer: { textAlign: 'center', fontSize: 10, color: '#999', marginTop: 12, borderTop: '1px solid #eee', paddingTop: 6 },
}

// ─── Uma página de comprador ──────────────────────────────────────────────────
const DEFAULT_COLUNAS_V1_COMP = ['lote', 'vendedor', 'curral', 'situacao', 'qtd', 'comissao', 'valor_unitario', 'total', 'total_com_comissao']

function MapaPage({ comp, leilao, organizador, showDbVendedor, dbOverride, colunasAtivas = DEFAULT_COLUNAS_V1_COMP, destaqueTotalCompradores = 'liquido', ocultarTotalLiquido = false }) {
  const mostrarCurral        = colunasAtivas.includes('curral')
  const mostrarPct           = colunasAtivas.includes('pct')
  // Só defesas (nenhuma venda): comissão é de defesa e não existe "Total – C. Venda"
  const soDefesa             = (comp.lotes || []).length > 0 && !(comp.lotes || []).some(l => l.situacao === 'VENDA')
  const mostrarTotalComissao = colunasAtivas.includes('total_com_comissao') && !soDefesa
  const mostrarSituacao      = colunasAtivas.includes('situacao')
  const mostrarQtd           = colunasAtivas.includes('qtd')
  const mostrarComissao      = colunasAtivas.includes('comissao')
  const mostrarValorUnit     = colunasAtivas.includes('valor_unitario')
  const mostrarTotal         = colunasAtivas.includes('total')
  const lotes = comp.lotes || []
  const criasTot = criasTotais(lotes)
  const nColsTbl = 1 + [mostrarCurral, colunasAtivas.includes('vendedor'), mostrarSituacao, mostrarQtd, mostrarPct, mostrarComissao, mostrarValorUnit, mostrarTotal, mostrarTotalComissao].filter(Boolean).length

  let totalVv = 0, totalCv = 0, totalCc = 0, totalDesp = 0, totalDesconto = 0
  let totalDefVv = 0, totalDefCv = 0
  let qtdCompras = 0, qtdDefesas = 0, qtdAnimais = 0, qtdAnimaisD = 0
  let totalFemea = 0, qtdFemea = 0, totalMacho = 0, qtdMacho = 0, totalMisto = 0, qtdMisto = 0

  lotes.forEach(l => {
    if (l.situacao === 'VENDA') {
      totalVv += l.vv; totalCv += l.cv_val; totalCc += l.cc_val
      totalDesp += l.desp; totalDesconto += (l.desconto || 0); qtdCompras++; qtdAnimais += l.qtd
    } else {
      totalDefVv += l.vv; totalDefCv += l.cv_val
      qtdDefesas++; qtdAnimaisD += l.qtd
    }
    // Médias por sexo (apenas lotes de VENDA)
    if (l.situacao === 'VENDA') {
      const sexo = (l.sexo || '').toUpperCase()
      if (sexo.includes('FEM') || sexo.includes('FÊMEA')) { totalFemea += l.vv; qtdFemea += l.qtd }
      else if (sexo.includes('MACH') || sexo.includes('MACHO')) { totalMacho += l.vv; qtdMacho += l.qtd }
      else { totalMisto += l.vv; qtdMisto += l.qtd }
    }
  })
  const fluxoLeilao = soDefesa ? '' : (leilao.fluxo_comissao || '')
  const despSomavel = fluxoLeilao === 'separada' ? 0 : totalDesp
  const totalGeral  = totalVv + totalDefVv
  const totalComCc  = totalGeral + totalCc + despSomavel - totalDesconto
  const totalComiss = totalCc + totalCv + totalDefCv + despSomavel

  // ── Cálculo GTA + Nota (fluxo gta_nota) ──────────────────────────────────
  // Isenção é POR LOTE e INDEPENDENTE por componente (gta_isento / nota_isento),
  // igual ao backend (reports.base.calcular_gta_nota).
  const gtaIsento = !!comp.gta_isento && !!comp.nota_isento  // tudo isento → esconde bloco
  let totalGtaNota = 0
  let gtaTotal = 0
  let notaTotal = 0
  let qtdVendedoresDistintos = 0

  if (fluxoLeilao === 'gta_nota' && !gtaIsento) {
    const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
    const valorNota = parseFloat(leilao.valor_nota) || 0
    const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'

    const vendaLotes = lotes.filter(l => l.situacao === 'VENDA')
    const lotesGta = vendaLotes.filter(l => !l.gta_isento)
    const lotesNota = vendaLotes.filter(l => !l.nota_isento)

    gtaTotal = valorPorAnimal * lotesGta.reduce((s, l) => s + (l.qtd || 0), 0)

    if (gtaModo === 'por_lote') {
      notaTotal = valorNota * lotesNota.length
    } else {
      const vendedoresSet = new Set()
      lotesNota.forEach(l => { if (l.vendedor_id) vendedoresSet.add(l.vendedor_id) })
      qtdVendedoresDistintos = vendedoresSet.size
      notaTotal = valorNota * qtdVendedoresDistintos
    }

    totalGtaNota = gtaTotal + notaTotal
  }

  const parcelas = []
  lotes.forEach(l => {
    // Dados bancários do vendedor deste lote
    const dbs = l.vendedor_dados_bancarios || []
    const dbLote = dbs.find(d => d.is_principal) || dbs[0] || null
    ;(l.parcelas || []).forEach(p => {
      parcelas.push({
        vendedor: l.vendedor_nome || '',
        db: dbLote,
        data: p.data,
        prazo: p.prazo,
        liquido: p.liquido,
        numero_lote: l.numero_lote,
      })
    })
  })

  // Agrupa parcelas do mesmo vendedor com mesma data de vencimento
  const normalizeDate = (d) => {
    if (!d) return ''
    const s = String(d)
    if (s.includes('T')) return s.split('T')[0]
    return s.slice(0, 10)
  }
  const groupedMap = {}
  parcelas.forEach(p => {
    const key = `${p.vendedor}|${normalizeDate(p.data)}`
    if (groupedMap[key]) {
      groupedMap[key].liquido += p.liquido
      if (p.numero_lote && !groupedMap[key].lotes_parcela.includes(p.numero_lote)) {
        groupedMap[key].lotes_parcela.push(p.numero_lote)
      }
    } else {
      groupedMap[key] = { ...p, data: normalizeDate(p.data), lotes_parcela: [p.numero_lote].filter(Boolean) }
    }
  })
  const parcelasAgrupadas = Object.values(groupedMap).sort((a, b) => (a.data || '').localeCompare(b.data || ''))
  const totalLiquido = parcelasAgrupadas.reduce((s, p) => s + p.liquido, 0)

  // Dados bancários do vendedor
  let dbVend = null
  if (showDbVendedor) {
    if (dbOverride && Object.values(dbOverride).some(v => v && v.trim())) {
      dbVend = dbOverride
    } else {
      for (const l of lotes) {
        const dbs = l.vendedor_dados_bancarios || []
        if (dbs.length) { dbVend = dbs.find(d => d.is_principal) || dbs[0]; break }
      }
    }
  }

  const orgDb = organizador.dados_bancarios
  const logoSrc = organizador.logo_url
    ? (organizador.logo_url.startsWith('http')
        ? organizador.logo_url
        : `${API_BASE}${organizador.logo_url.startsWith('/') ? '' : '/'}${organizador.logo_url}`)
    : null

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.hdr}>
        <div style={{ flex: '0 0 160px' }}>
          {logoSrc
            ? <img src={logoSrc} alt="logo" style={S.hdrLogo} />
            : <span style={{ fontWeight: 700, fontSize: 15 }}>{organizador.nome}</span>}
        </div>
        <div style={{ ...S.hdrTitle, flex: 1 }}>
          <div style={S.hdrT1}>MAPA DO COMPRADOR</div>
          <div style={S.hdrT2}>{organizador.nome}</div>
          <div style={S.hdrT2}>{leilao.nome} — {fmtDate(leilao.data_leilao)}</div>
        </div>
        <div style={{ flex: '0 0 160px', textAlign: 'right' }}>
          {leilao.banner_url ? (
            <img
              src={leilao.banner_url.startsWith('http') ? leilao.banner_url : `${API_BASE}${leilao.banner_url.startsWith('/') ? '' : '/'}${leilao.banner_url}`}
              alt="banner leilão"
              style={{ maxHeight: 64, maxWidth: 160, objectFit: 'contain', marginLeft: 'auto' }}
            />
          ) : (
            <div style={S.hdrBank}>
              {organizador.telefone && <div>Tel: {organizador.telefone}</div>}
              {organizador.endereco && <div>{organizador.endereco}</div>}
              {(organizador.cidade || organizador.uf) && <div>{[organizador.cidade, organizador.uf].filter(Boolean).join(' - ')}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Nome do comprador centralizado */}
      <div style={{ border: '1px solid #999', padding: '1.5px 12px', textAlign: 'center', marginBottom: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{comp.nome.toUpperCase()}</div>
        {(comp.gta_isento || comp.nota_isento) && (
          <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 7px', letterSpacing: 0.3 }}>
            {comp.gta_isento && comp.nota_isento ? 'GTA/NOTA ISENTOS' : comp.gta_isento ? 'GTA ISENTA' : 'NOTA ISENTA'}
          </span>
        )}
      </div>

      {/* Dados comprador + fazenda */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 14, fontSize: 12, lineHeight: 1.6 }}>
        <div style={{ flex: 1 }}>
          <div><strong>CPF/CNPJ:</strong> {comp.cpf_cnpj || '–'}</div>
          <div><strong>Cidade:</strong> {comp.cidade ? `${comp.cidade.toUpperCase()}${comp.uf ? ` - ${comp.uf.toUpperCase()}` : ''}` : '–'}</div>
          <div><strong>Tel:</strong> {comp.telefone || '–'}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div><strong>Fazenda:</strong> {comp.fazenda ? comp.fazenda.toUpperCase() : '–'}</div>
          <div><strong>IE:</strong> {comp.fazenda_ie || '–'}</div>
          <div><strong>Cidade:</strong> {comp.fazenda_cidade ? `${comp.fazenda_cidade.toUpperCase()}${comp.fazenda_uf ? ` - ${comp.fazenda_uf.toUpperCase()}` : ''}` : '–'}</div>
        </div>
      </div>

      {/* Tabela lotes */}
      <table style={S.tbl}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: '5%' }}>Lote</th>
            {mostrarCurral && <th style={{ ...S.th, width: '7%' }}>Curral</th>}
            {colunasAtivas.includes('vendedor') && <th style={{ ...S.th }}>Vendedor</th>}
            {mostrarSituacao && <th style={{ ...S.th, width: '8%' }}>Situação</th>}
            {mostrarQtd && <th style={{ ...S.th, width: '5%' }}>Qtd</th>}
            {mostrarPct && <th style={{ ...S.th, width: '6%' }}>%</th>}
            {mostrarComissao && <th style={{ ...S.th, width: '10%' }}>{soDefesa ? 'Com. Defesa' : fluxoLeilao === 'separada' ? 'Com. Compra' : 'Com. Venda'}</th>}
            {mostrarValorUnit && <th style={{ ...S.th, width: '11%' }}>Valor<br />Unitário</th>}
            {mostrarTotal && <th style={{ ...S.th, width: '10%' }}>Total</th>}
            {mostrarTotalComissao && <th style={{ ...S.th, width: '12%' }}>{fluxoLeilao === 'separada' ? 'Total +\nCom. Comp.' : 'Total –\nC. Venda'}</th>}
          </tr>
        </thead>
        <tbody>
          {lotes.map((l, i) => {
            const desc = [l.tipo_animal, l.raca, l.sexo, l.idade].filter(Boolean).join(' ').toUpperCase()
            return (
              <Fragment key={i}>
              <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={S.tdC}><strong>{l.numero_lote}</strong></td>
                {mostrarCurral && <td style={S.tdC}>{l.curral || '–'}</td>}
                {colunasAtivas.includes('vendedor') && (
                  <td style={S.td}>
                    <div style={{ fontWeight: 700 }}>{l.vendedor_nome.toUpperCase()}</div>
                    {desc && <div style={{ ...S.desc, fontWeight: 700 }}>{desc}</div>}
                  </td>
                )}
                {mostrarSituacao && <td style={S.tdC}>{l.situacao}</td>}
                {mostrarQtd && <td style={S.tdC}>{l.qtd}</td>}
                {mostrarPct && <td style={S.tdR}>{fluxoLeilao === 'separada' ? (l.cc_pct != null ? `${Number(l.cc_pct).toFixed(1)}%` : '–') : (l.cv_pct != null ? `${Number(l.cv_pct).toFixed(1)}%` : '–')}</td>}
                {mostrarComissao && <td style={S.tdR}>{brl(fluxoLeilao === 'separada' ? l.cc_val : l.cv_val)}</td>}
                {mostrarValorUnit && <td style={S.tdR}>{brl(l.vv_unit)}</td>}
                {mostrarTotal && <td style={S.tdR}>{num(l.vv)}</td>}
                {mostrarTotalComissao && <td style={S.tdR}>{brl(fluxoLeilao === 'separada' ? l.vv + l.cc_val : l.total_cv)}</td>}
              </tr>
              {criasTxt(l) && (
                <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td colSpan={nColsTbl} style={{ ...S.td, fontSize: 10, padding: '1px 6px' }}>
                    <strong>Crias:</strong> {criasTxt(l)}
                  </td>
                </tr>
              )}
              </Fragment>
            )
          })}
        </tbody>
      </table>

      {/* Totalizadores — três colunas */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 6, fontSize: 12 }}>
        {/* Esquerda: resumo de lotes */}
        <div style={{ flex: 1, padding: '4px 14px', lineHeight: 1.7, textAlign: 'center' }}>
          <div><strong>Compras:</strong> Lotes: {qtdCompras}, Qtde: {qtdAnimais}</div>
          <div><strong>Defesas:</strong> Lotes: {qtdDefesas}, Qtde: {qtdAnimaisD}</div>
          <div><strong>Quantidade animais:</strong> {qtdAnimais + qtdAnimaisD}</div>
          {criasTot.txt && <div><strong>Crias:</strong> {criasTot.txt}</div>}
          {fluxoLeilao !== 'separada' && <div><strong>Despesas:</strong> {brl(totalDesp)}</div>}
        </div>
        {/* Centro: comissões */}
        <div style={{ flex: 1, padding: '4px 14px', lineHeight: 1.7, textAlign: 'center' }}>
          <div><strong>Comissão da compra:</strong> {brl(totalCc)}</div>
          {fluxoLeilao !== 'separada' && <div><strong>Comissão venda:</strong> {brl(totalCv)}</div>}
          {fluxoLeilao !== 'separada' && qtdDefesas > 0 && <div><strong>Comissão defesa:</strong> {brl(totalDefCv)}</div>}
          {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 && <div><strong>(GTA + NOTA):</strong> {brl(totalGtaNota)}</div>}
          {totalDesconto > 0 && <div style={{ color: '#92400e' }}><strong>Desconto À Vista:</strong> -{brl(totalDesconto)}</div>}
        </div>
        {/* Direita: médias por sexo */}
        <div style={{ flex: 1, padding: '4px 14px', lineHeight: 1.7, textAlign: 'center' }}>
          {(() => {
            const mediaFemea = qtdFemea > 0 ? (totalFemea + (qtdFemea / qtdAnimais) * (totalCc + totalGtaNota)) / qtdFemea : 0
            const mediaMacho = qtdMacho > 0 ? (totalMacho + (qtdMacho / qtdAnimais) * (totalCc + totalGtaNota)) / qtdMacho : 0
            const mediaMisto = qtdMisto > 0 ? (totalMisto + (qtdMisto / qtdAnimais) * (totalCc + totalGtaNota)) / qtdMisto : 0
            const mediaGeral = qtdAnimais > 0 ? (totalVv + totalCc + totalGtaNota) / qtdAnimais : 0
            return (
              <>
                <div><strong>Média Fêmea:</strong> {brl(mediaFemea)}</div>
                <div><strong>Média Macho:</strong> {brl(mediaMacho)}</div>
                {qtdMisto > 0 && <div><strong>Média Misto:</strong> {brl(mediaMisto)}</div>}
                <div style={{ fontWeight: 700, marginTop: 4 }}><strong>Média Geral:</strong> {brl(mediaGeral)}</div>
              </>
            )
          })()}
        </div>
      </div>

      {/* Tabela parcelas */}
      {parcelasAgrupadas.length > 0 && (
        <>
          <table style={S.tbl}>
            <thead>
              <tr>
                <th style={{ ...S.thPar, width: '5%' }}>#</th>
                <th style={{ ...S.thPar, width: '30%' }}>Vendedor</th>
                <th style={{ ...S.thPar, width: '15%' }}>Vencimento</th>
                <th style={{ ...S.thPar, width: '15%' }}>Prazo</th>
                <th style={{ ...S.thPar, width: '10%' }}>Lotes</th>
                <th style={{ ...S.thPar, width: '20%' }}>Líquido</th>
              </tr>
            </thead>
            <tbody>
              {parcelasAgrupadas.map((p, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={S.tdC}>{i + 1}</td>
                  <td style={S.td}>
                    <div style={{ fontWeight: 700 }}>{p.vendedor.toUpperCase()}</div>
                    {showDbVendedor && p.db && (
                      <div style={{ fontSize: 10, color: '#333', marginTop: 2, lineHeight: 1.4 }}>
                        {p.db.cpf_cnpj_titular && <div>CPF/CNPJ: {p.db.cpf_cnpj_titular}</div>}
                        {p.db.banco && <div>BANCO: {p.db.banco.toUpperCase()}</div>}
                        {p.db.agencia && <div>AG: {p.db.agencia}  C/C: {p.db.conta}</div>}
                        {p.db.is_terceiro && p.db.titular && <div>TITULAR: {p.db.titular.toUpperCase()}</div>}
                        {p.db.observacoes && <div>OBS: {p.db.observacoes}</div>}
                        {p.db.pix && <div style={{ fontWeight: 700, fontSize: 11, marginTop: 3, paddingTop: 2, borderTop: '1px solid #ccc' }}>PIX: {p.db.pix.toUpperCase()}</div>}
                      </div>
                    )}
                  </td>
                  <td style={S.tdC}>{fmtDate(p.data)}</td>
                  <td style={S.tdC}>{p.prazo}</td>
                  <td style={S.tdC}>{(p.lotes_parcela || []).join(', ')}</td>
                  <td style={S.tdR}>{brl(p.liquido)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* TOTAL GERAL */}
          <div style={{ border: '1px solid #999', marginTop: 6, marginBottom: 8 }}>
            <div style={{ background: '#f0f0f0', borderBottom: '1px solid #999', padding: '2px 0', textAlign: 'center', fontWeight: 700, fontSize: 13 }}>
              TOTAL GERAL
            </div>
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Animais</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{qtdAnimais + qtdAnimaisD}</div>
                {criasTot.txt && <div style={{ fontSize: 10, color: '#555' }}>Crias: {criasTot.txt}</div>}
              </div>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Compra</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalGeral)}</div>
              </div>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>
                  {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Com./Taxa Geral' : 'Total Comissões'}
                </div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {brl(fluxoLeilao === 'separada' ? totalCc : fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? totalComiss + totalGtaNota : totalComiss)}
                </div>
              </div>
              {fluxoLeilao === 'separada' ? (
                <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', background: '#1a3a5c', color: '#fff' }}>
                  <div style={{ fontSize: 10, marginBottom: 2, opacity: 0.85 }}>
                    {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Gado + Com. + Taxas' : 'Total + Comissões'}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? totalComCc + totalGtaNota : totalComCc)}</div>
                </div>
              ) : destaqueTotalCompradores === 'total_mais_comissoes' ? (
                <>
                  {!ocultarTotalLiquido && (
                    <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                      <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Líquido</div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalLiquido)}</div>
                    </div>
                  )}
                  <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', background: '#1a3a5c', color: '#fff' }}>
                    <div style={{ fontSize: 10, marginBottom: 2, opacity: 0.85 }}>
                      {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Gado + Com. + Taxas' : 'Total + Comissões'}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? totalComCc + totalGtaNota : totalComCc)}</div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', ...(!ocultarTotalLiquido ? { borderRight: '1px solid #ddd' } : {}) }}>
                    <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>
                      {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Gado + Com. + Taxas' : 'Total + Comissões'}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? totalComCc + totalGtaNota : totalComCc)}</div>
                  </div>
                  {!ocultarTotalLiquido && (
                    <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', background: '#1a3a5c', color: '#fff' }}>
                      <div style={{ fontSize: 10, marginBottom: 2, opacity: 0.85 }}>Total Líquido</div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalLiquido)}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {/* Total de comissões + Dados bancários do organizador */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, marginBottom: 8 }}>
            <tbody>
              <tr>
                <td style={{ background: '#f0f0f0', border: '1px solid #ccc', padding: '4px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13 }}>
                  {fluxoLeilao === 'gta_nota' && totalGtaNota > 0
                    ? <span>TOTAL DE COMISSÕES + GTA/NOTA: <span style={{ fontSize: 15 }}>{brl(totalComiss + totalGtaNota)}</span></span>
                    : fluxoLeilao === 'separada'
                      ? <span>TOTAL DE COMISSÕES: <span style={{ fontSize: 15 }}>{brl(totalCc)}</span></span>
                      : <span>TOTAL DE COMISSÕES: <span style={{ fontSize: 15 }}>{brl(totalComiss)}</span></span>
                  }
                  {orgDb && (
                    <span>
                      {' | '}BANCO: {orgDb.banco}{orgDb.agencia ? ` | AG: ${orgDb.agencia}  C/C: ${orgDb.conta}` : ''}{orgDb.pix ? ` | PIX: ${orgDb.pix}` : ''}{orgDb.observacoes?.trim() ? ` | OBS: ${orgDb.observacoes.split('\n').map(s => s.trim()).filter(Boolean).join(' - ')}` : ''}
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* Dados bancários vendedor — bloco separado só quando não há parcelas */}
      {dbVend && parcelasAgrupadas.length === 0 && (
        <div style={S.dbVend}>
          <strong>DADOS BANCÁRIOS DO VENDEDOR</strong>&nbsp;&nbsp;
          Banco: {dbVend.banco || '–'}&nbsp;&nbsp;
          Agência: {dbVend.agencia || '–'}&nbsp;&nbsp;
          Conta: {dbVend.conta || '–'}&nbsp;&nbsp;
          PIX: {dbVend.pix || '–'}
        </div>
      )}

      {/* Assinatura */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, fontSize: 13 }}>
        <div>
          <div>{[organizador?.cidade, organizador?.estado].filter(Boolean).join(' - ')}{organizador?.cidade || organizador?.estado ? ', ' : ''}{fmtDate(leilao?.data_leilao)}</div>
          <div style={{ color: '#1a3c6b', marginTop: 4 }}>Declaro que estou de acordo com as especificações acima.</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: 220, margin: '0 auto 4px' }} />
          <div style={{ fontWeight: 700 }}>{comp.nome}</div>
          <div style={{ color: '#999', fontSize: 10 }}>Assinatura do Comprador</div>
        </div>
      </div>

      <div style={S.footer}>www.rematei.com.br</div>
    </div>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function MapaCompradorPreview({
  dados,
  showDbVendedor,
  dbOverride,
  colunasAtivas = DEFAULT_COLUNAS_V1_COMP,
  destaqueTotalCompradores = 'liquido',
  ocultarTotalLiquido = false,
  orgId,
  leilaoId,
  compradorId,
  tipo,
  onReload,
  ordenacao = 'numero_lote',
}) {
  const iframeRef = useRef(null)
  const [showAlterarComissao, setShowAlterarComissao] = useState(false)
  const [pdfUrl,      setPdfUrl]      = useState(null)
  const [loadingPdf,  setLoadingPdf]  = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [showGtaModal, setShowGtaModal] = useState(false)

  if (!dados) return null
  const { leilao, organizador, compradores } = dados

  const fluxo = leilao?.fluxo_comissao || ''
  const mostraBotaoGta = fluxo === 'gta_nota' && compradores.length === 1 && compradorId
  const compGta  = compradores.length === 1 ? !!compradores[0]?.gta_isento  : false
  const compNota = compradores.length === 1 ? !!compradores[0]?.nota_isento : false

  // GTA e Nota que AINDA são cobrados do 1º comprador (para exibir no modal)
  const { gtaNoModal, notaNoModal } = (() => {
    if (!mostraBotaoGta || !compradores[0]) return { gtaNoModal: 0, notaNoModal: 0 }
    const comp = compradores[0]
    const venda = (comp.lotes || []).filter(l => l.situacao === 'VENDA')
    const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
    const valorNota = parseFloat(leilao.valor_nota) || 0
    const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
    const lotesGta = venda.filter(l => !l.gta_isento)
    const lotesNota = venda.filter(l => !l.nota_isento)
    const gta = valorPorAnimal * lotesGta.reduce((s, l) => s + (l.qtd || 0), 0)
    const nota = gtaModo === 'por_lote'
      ? valorNota * lotesNota.length
      : valorNota * new Set(lotesNota.filter(l => l.vendedor_id).map(l => l.vendedor_id)).size
    return { gtaNoModal: gta, notaNoModal: nota }
  })()

  // ── Busca PDF do backend e abre modal com iframe ──────────────────────────
  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const qs = new URLSearchParams()
      if (compradorId) qs.set('comprador_id', compradorId)
      qs.set('tipo', tipo || 'padrao')
      qs.set('dados_bancarios_vendedor', String(showDbVendedor))
      qs.set('ordenacao', ordenacao)
      if (showDbVendedor && dbOverride) {
        if (dbOverride.banco)   qs.set('db_banco',   dbOverride.banco)
        if (dbOverride.agencia) qs.set('db_agencia', dbOverride.agencia)
        if (dbOverride.conta)   qs.set('db_conta',   dbOverride.conta)
        if (dbOverride.pix)     qs.set('db_pix',     dbOverride.pix)
      }
      const resp = await api.get(
        `/organizadores/${orgId}/mapas/leilao/${leilaoId}/mapa-comprador?${qs}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      openPdf(url, setPdfUrl, setShowPdfModal)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => {
    setShowPdfModal(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  // Nome do arquivo: "Mapa do Comprador - NOME DO COMPRADOR.pdf"
  const pdfFilename = (() => {
    const nomeComp = compradores.length === 1
      ? compradores[0].nome
      : `Todos (${compradores.length})`
    return `Mapa do Comprador - ${nomeComp}.pdf`
      .replace(/[/\\?%*:|"<>]/g, '-')  // remove chars inválidos
  })()

  return (
    <>
      {/* ── Barra de ação ── */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-600 flex-1 min-w-0 truncate">
          <span className="font-medium text-gray-800">{leilao.nome}</span>
          {' · '}
          {compradores.length === 1 ? compradores[0].nome : `${compradores.length} compradores`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleImprimir}
            disabled={loadingPdf}
            className="flex items-center gap-1.5 px-4 py-2.5 sm:px-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            <Printer className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{loadingPdf ? 'Gerando...' : 'Imprimir'}</span>
            <span className="sm:hidden">{loadingPdf ? 'Gerando...' : 'PDF'}</span>
          </button>
          {compradores.length === 1 && compradorId && (
            <PrazoComissao orgId={orgId} leilaoId={leilaoId} fluxo={fluxo} comprador={compradores[0]} onChanged={onReload} />
          )}
          {compradores.length === 1 && (
            <>
              <button onClick={async () => {
                try {
                  const resp = await api.get(
                    `/organizadores/${orgId}/recibos/mapa-comprador/${leilaoId}/${compradores[0].id}/recibo-comissao`,
                    { responseType: 'blob', timeout: 30000 }
                  )
                  const blob = new Blob([resp.data], { type: 'application/pdf' })
                  if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                  openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
                } catch (err) {
                  import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de comissão'))
                }
              }}
                className="flex items-center gap-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Recibo de recebimento de comissões + despesas">
                Recibo Comissão
              </button>
              <button onClick={async () => {
                try {
                  const resp = await api.get(
                    `/organizadores/${orgId}/recibos/mapa-comprador/${leilaoId}/${compradores[0].id}/recibo-repasse-todos`,
                    { responseType: 'blob', timeout: 60000 }
                  )
                  const blob = new Blob([resp.data], { type: 'application/pdf' })
                  if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                  openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
                } catch (err) {
                  import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de repasse'))
                }
              }}
                className="flex items-center gap-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Recibo de recebimento do valor a repassar para todos os vendedores">
                Recibo Repasse
              </button>
              <button onClick={() => setShowAlterarComissao(true)}
                className="hidden sm:flex items-center gap-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Alterar comissão de compra dos lotes">
                Alterar Com.
              </button>
              {mostraBotaoGta && (() => {
                const algum = compGta || compNota
                const txt = compGta && compNota ? 'GTA/Nota isentos'
                  : compGta ? 'GTA isento'
                  : compNota ? 'Nota isenta'
                  : 'Isentar GTA/Nota'
                return (
                  <button
                    onClick={() => setShowGtaModal(true)}
                    className={`flex items-center gap-1 px-3 py-2 text-white text-xs font-medium rounded-lg transition-colors ${algum ? 'bg-blue-500 hover:bg-blue-600' : 'bg-teal-600 hover:bg-teal-700'}`}
                    title="Isentar/reverter GTA e Nota do comprador"
                  >
                    {txt}
                  </button>
                )
              })()}
            </>
          )}
        </div>
      </div>

      {/* ── Aviso mobile (oculto em sm+) ── */}
      <div className="sm:hidden bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>O preview é otimizado para desktop.</p>
        <p className="mt-1">Use os botões acima para gerar o PDF.</p>
      </div>

      {/* ── Preview JSX em tamanho real (oculto no mobile) ── */}
      <div className="hidden sm:block" style={{ background: '#f3f4f6', padding: '24px 0' }}>
        {compradores.map((comp, i) => (
          <div
            key={comp.id || i}
            style={{
              maxWidth: 900,
              margin: '0 auto 24px',
              background: '#fff',
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <MapaPage
              comp={comp}
              leilao={leilao}
              organizador={organizador}
              showDbVendedor={showDbVendedor}
              dbOverride={dbOverride}
              colunasAtivas={colunasAtivas}
              destaqueTotalCompradores={destaqueTotalCompradores}
              ocultarTotalLiquido={ocultarTotalLiquido}
            />
          </div>
        ))}
      </div>

      {/* ── Modal PDF para impressão ── */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            {/* Header modal */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa do Comprador — {leilao.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Salvar PDF com nome correto */}
                <button
                  onClick={() => downloadPdf(pdfUrl, pdfFilename)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Salvar PDF
                </button>
                <button
                  onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir
                </button>
                <button
                  onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                >
                  Fechar
                </button>
              </div>
            </div>
            {/* iframe PDF */}
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe
                ref={iframeRef}
                src={pdfUrl}
                title={pdfFilename}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Isentar/Reverter GTA */}
      {showGtaModal && compradores.length === 1 && (
        <IsentarGTAModal
          compradorNome={compradores[0].nome}
          compradorId={compradorId}
          leilaoId={leilaoId}
          orgId={orgId}
          gtaIsento={compGta}
          notaIsento={compNota}
          totalGta={gtaNoModal}
          totalNota={notaNoModal}
          totalGtaNota={gtaNoModal + notaNoModal}
          onClose={() => setShowGtaModal(false)}
          onSaved={() => { setShowGtaModal(false); if (onReload) onReload() }}
        />
      )}

      {/* Modal Alterar Comissão */}
      {showAlterarComissao && compradores.length === 1 && (
        <AlterarComissaoModal
          tipo="compra"
          lotes={compradores[0].lotes.filter(l => l.situacao !== 'DEFESA')}
          organizadorId={orgId}
          onClose={() => setShowAlterarComissao(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}
    </>
  )
}
